/*
  Cart upsell carousel.

  Shopify's recommendations route answers about one product at a time, so this
  asks about the last couple of products in the cart and merges the answers in
  turn, so both are represented near the front of the row.

  Dropped from the result: anything already in the cart, and the same style in
  another colour — offering the red one to someone who just put the navy one in
  the cart reads as a mistake.

  Adding happens in place. One size left, and the plus button adds it; more than
  one, and the sizes open in a strip under the row. Nothing opens over the cart,
  which on a phone is the whole point: what is already in the cart has to stay
  the clearest thing on the screen.
*/
(function () {
  'use strict';

  if (customElements.get('cart-upsell')) return;

  /** How many of the cart's products we ask about. */
  var SOURCE_LIMIT = 2;
  /** How many recommendations we ask for per product. */
  var PER_SOURCE = 6;
  /** The longest merged list we keep; each carousel takes what it needs. */
  var MAX_CARDS = 16;

  /**
   * Cards already fetched, keyed by the cart they were fetched for. The drawer
   * rebuilds itself on every quantity change, which builds a new element each
   * time; without this, changing a quantity would fetch an unchanged list again.
   */
  var cache = new Map();

  /**
   * The visitor's side of the test, kept so it does not change on them. The
   * name changes when the split does: under the old name half the visitors
   * would keep the 50/50 draw for another month.
   */
  var COOKIE = 'kds_upsell_ab2';
  var COOKIE_DAYS = 30;
  var reported = false;

  function readBucket() {
    var parts = document.cookie.split(';');

    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part.indexOf(COOKIE + '=') !== 0) continue;

      var value = part.slice(COOKIE.length + 1);
      if (value === 'a' || value === 'b') return value;
    }

    return null;
  }

  function writeBucket(bucket) {
    var expires = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
    try {
      document.cookie = COOKIE + '=' + bucket + ';expires=' + expires + ';path=/;SameSite=Lax';
    } catch (e) {
      /* a visitor who refuses cookies simply gets a fresh draw next time */
    }
  }

  /**
   * Analytics needs to know which side of the test a visitor is on, or the two
   * cannot be compared. The bucket goes out as a user property, so every
   * session of that visitor carries it, and once as an event, so the test can
   * be found in the reports before the property is registered.
   */
  function report(bucket) {
    if (reported) return;
    reported = true;

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('set', 'user_properties', { upsell_bucket: bucket });
        window.gtag('event', 'upsell_bucket', { bucket: bucket });
      } else if (window.dataLayer) {
        window.dataLayer.push({ event: 'upsell_bucket', upsell_bucket: bucket });
      }
    } catch (e) {
      /* measurement must never be the reason a cart breaks */
    }
  }

  /**
   * The bucket and whatever the row sold are written onto the cart, so they
   * travel with the order into Shopify. Analytics cannot do this part: the
   * purchase is reported from the checkout, a context this page never reaches,
   * so a property set here would never reach the order it is meant to explain.
   */
  function markCart(attributes) {
    try {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {});
    } catch (e) {
      return null;
    }
  }

  function remember(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function recall(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sendEvent(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
    } catch (e) {}
  }

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-upsell-track]');
      this.viewport = this.querySelector('.cart-upsell__viewport');
      this.sizes = this.querySelector('[data-upsell-sizes]');
      this.sizesTitle = this.querySelector('[data-upsell-sizes-title]');
      this.sizesRow = this.querySelector('[data-upsell-sizes-row]');
      this.status = this.querySelector('[data-upsell-status]');
      this.cartIds = (this.dataset.cartIds || '').split(',').filter(Boolean);
      this.limit = parseInt(this.dataset.limit, 10) || 10;
      this.collectionUrl = this.dataset.collectionUrl || '';
      this.recent = this.dataset.recent === '1';
      this.source = this.dataset.source || 'cart row';
      // Which row this is, in the shop's own words: a collection handle, the
      // recently-viewed list, or the recommendations. Every event and every
      // line it sells carries it, so "which row sells" is a question the
      // reports can answer rather than one we guess at.
      //
      // The event parameters are named upsell_source and upsell_row, not
      // source and row: GA4 sends its own `source` on page_view, session_start
      // and the rest - the traffic source - so a dimension bound to the bare
      // name comes back full of facebook, google and shopify_email with our
      // two values lost among them. Seen in a report before it mattered.
      this.row = this.dataset.row || 'recommendations';

      if (!this.track) return;
      if (!this.collectionUrl && !this.recent && !this.cartIds.length) return;

      // Only the row under the cart is in the test, whatever it is showing.
      // The two in the empty drawer stand in for a button that was always
      // there, so hiding them from half the visitors would take something
      // away rather than try something new. Liquid says which is which by
      // whether it wrote the test's settings onto the element.
      if (!this.dataset.mode) {
        this.bucket = this.recent ? 'recently viewed' : 'collection';
      } else {
        this.bucket = this.decideBucket();
        this.stampBucket();
        if (this.bucket === 'b') {
          this.remove();
          return;
        }
      }

      this.addEventListener('click', this.onClick.bind(this));
      this.enableSideScroll();

      // An emptied cart has nothing to suggest around, and the cart page does
      // not rebuild this row on its own. The collection row is the opposite:
      // it belongs to the empty cart and the drawer rebuilds it by itself.
      if (this.dataset.mode) {
        this.onCartRefreshed = function (event) {
          if (event.detail && event.detail.itemCount === 0) this.remove();
        }.bind(this);
        document.addEventListener('cart:refreshed', this.onCartRefreshed);
      }

      // The drawer is part of every page, so it waits to be opened. On the cart
      // page the row is part of what the shopper came for, so it loads at once.
      var drawer = this.closest('cart-drawer');
      if (drawer) {
        this.waitForDrawer(drawer);
      } else {
        this.load();
      }
    }

    /**
     * Who sees the row. Off and on-for-everyone are settings; in a test the
     * visitor is drawn once and kept on that side by a cookie, so the row does
     * not appear one day and vanish the next for the same person.
     */
    decideBucket() {
      if (this.dataset.mode !== 'test') return 'a';

      var bucket = readBucket();
      if (!bucket) {
        var split = parseInt(this.dataset.split, 10);
        if (isNaN(split)) split = 50;
        bucket = Math.random() * 100 < split ? 'a' : 'b';
        writeBucket(bucket);
      }

      report(bucket);
      return bucket;
    }

    /** Writes the bucket onto the cart once, so the order carries it. */
    stampBucket() {
      if (this.dataset.mode !== 'test') return;
      if (recall('kds_upsell_stamped') === this.bucket) return;

      remember('kds_upsell_stamped', this.bucket);
      markCart({ upsell_bucket: this.bucket });
    }

    /**
     * The drawer says it opened by taking a class, and that is the only sign
     * worth watching: until then it sits off the side of the page, where
     * waiting for the row to come on screen would never come back.
     */
    waitForDrawer(drawer) {
      if (drawer.classList.contains('active')) {
        this.load();
        return;
      }

      this.drawerObserver = new MutationObserver(
        function () {
          if (!drawer.classList.contains('active')) return;
          this.drawerObserver.disconnect();
          this.load();
        }.bind(this)
      );
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.onCartRefreshed) document.removeEventListener('cart:refreshed', this.onCartRefreshed);
    }

    /* ------------------------------------------------------------------ *
     * Getting the products
     * ------------------------------------------------------------------ */

    load() {
      if (this.recent) {
        this.loadRecent();
        return;
      }

      if (this.collectionUrl) {
        this.loadCollection();
        return;
      }

      var key = this.cartIds.join(',');
      var cached = cache.get(key);

      if (cached) {
        this.render(cached);
        return;
      }

      var sources = this.cartIds.slice().reverse().slice(0, SOURCE_LIMIT);

      Promise.all(
        sources.map(function (id) {
          return this.fetchFor(id);
        }, this)
      )
        .then(
          function (lists) {
            var cards = this.merge(lists).map(function (card) {
              return card.outerHTML;
            });
            cache.set(key, cards);
            this.render(cards);
          }.bind(this)
        )
        .catch(function () {
          /* a row that cannot load is simply not shown */
        });
    }

    /** What every event about this row says about itself. */
    rowParams(extra) {
      var params = {
        bucket: this.bucket,
        upsell_source: this.source,
        upsell_row: this.row
      };

      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) params[key] = extra[key];
      }
      return params;
    }

    lineLabel() {
      var named = this.row && this.row !== 'recommendations' && this.row !== 'recently-viewed';

      return named ? this.source + ' · ' + this.row : this.source;
    }

    /**
     * The cards go in while the drawer is still sliding into place, and a lazy
     * image that was off screen when it was inserted is never looked at again
     * until something scrolls - the drawer's own transform does not count. Left
     * alone the row sat there with empty squares under the titles, measured at
     * twenty seconds with nothing loaded. The ones that start on screen are
     * told to load now; the rest go on waiting for a thumb, which does count.
     */
    wakeFirstImages() {
      var imgs = this.track.querySelectorAll('.cup-card__img');
      var upTo = Math.min(imgs.length, 4);

      for (var i = 0; i < upTo; i++) imgs[i].loading = 'eager';
    }

    /**
     * The row scrolls sideways, which a thumb finds by itself and a mouse
     * never does: the scrollbar is hidden and a wheel scrolls the page past
     * it. So a wheel over the row moves the row, and the row can be dragged.
     * At either end the gesture is handed back, so the page keeps scrolling
     * the way it would have.
     */
    enableSideScroll() {
      var viewport = this.viewport;
      if (!viewport) return;

      // Did anyone look past the first card? Once per row: the interesting
      // thing is whether the row was worked at all, not how far it travelled.
      var scrolledReported = false;
      // Where the row starts, read now rather than on the first scroll event:
      // by the time that one arrives the row has already moved, and comparing
      // it against itself never clears the threshold.
      var scrollOrigin = viewport.scrollLeft;

      viewport.addEventListener(
        'scroll',
        function () {
          if (scrolledReported) return;
          if (Math.abs(viewport.scrollLeft - scrollOrigin) < 40) return;

          scrolledReported = true;
          sendEvent('upsell_scroll', this.rowParams({}));
        }.bind(this),
        { passive: true }
      );

      viewport.addEventListener(
        'wheel',
        function (event) {
          if (event.deltaX) return; // a trackpad already does the right thing
          if (viewport.scrollWidth <= viewport.clientWidth) return;

          // The shop reads right to left, where scrollLeft starts at 0 and
          // goes negative as the row advances. Scrolling the wheel down should
          // move the row forward whichever way the page runs.
          var forward = getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
          var before = viewport.scrollLeft;

          viewport.scrollLeft += event.deltaY * forward;
          if (viewport.scrollLeft !== before) event.preventDefault();
        },
        { passive: false }
      );

      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var moved = false;

      viewport.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch') return; // the browser does this one
        dragging = true;
        moved = false;
        startX = event.clientX;
        startLeft = viewport.scrollLeft;
      });

      viewport.addEventListener('pointermove', function (event) {
        if (!dragging) return;

        var travelled = event.clientX - startX;
        if (!moved && Math.abs(travelled) < 4) return;

        moved = true;
        viewport.classList.add('is-dragging');
        viewport.scrollLeft = startLeft - travelled;
        event.preventDefault();
      });

      // A drag that ends on a card is a drag, not a tap on the card.
      viewport.addEventListener(
        'click',
        function (event) {
          if (!moved) return;
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        },
        true
      );

      var stop = function () {
        dragging = false;
        viewport.classList.remove('is-dragging');
      };
      viewport.addEventListener('pointerup', stop);
      viewport.addEventListener('pointercancel', stop);
      viewport.addEventListener('pointerleave', stop);
    }

    /**
     * The empty cart's row. One request for a whole collection, already in the
     * order the shop sells it in - no merging, nothing to filter out, because
     * there is nothing in the cart to clash with.
     */
    loadCollection() {
      var key = 'collection:' + this.collectionUrl;
      var cached = cache.get(key);

      if (cached) {
        this.render(this.withoutCartItems(cached));
        return;
      }

      fetch(this.collectionUrl)
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .then(
          function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var cards = Array.prototype.slice.call(doc.querySelectorAll('.cup-card')).map(function (card) {
              return card.outerHTML;
            });
            cache.set(key, cards);
            this.render(this.withoutCartItems(cards));
          }.bind(this)
        )
        .catch(function () {
          /* a row that cannot load is simply not shown */
        });
    }

    /**
     * What this visitor looked at lately. The theme already keeps the list -
     * handles, newest first, written by the search modal - so this only has to
     * draw it. One small request per product, because there is no asking for
     * several by handle at once; the view they answer with is about a
     * kilobyte, which is what makes that acceptable. The search modal fetches
     * whole product pages for the same list, at roughly 350KB each.
     */
    loadRecent() {
      var handles = [];

      try {
        var stored = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
        if (Object.prototype.toString.call(stored) === '[object Array]') {
          handles = stored.filter(function (handle) {
            return typeof handle === 'string' && handle;
          });
        }
      } catch (e) {
        /* a browser that will not answer about storage simply has no list */
      }

      handles = handles.slice(0, this.limit);
      if (!handles.length) return;

      var base = (this.dataset.rootUrl || '/').replace(/[/]$/, '');

      Promise.all(
        handles.map(function (handle) {
          return fetch(base + '/products/' + encodeURIComponent(handle) + '?view=cart-upsell')
            .then(function (response) {
              return response.ok ? response.text() : '';
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var card = doc.querySelector('.cup-card');
              return card ? card.outerHTML : '';
            })
            .catch(function () {
              return '';
            });
        })
      )
        .then(
          function (cards) {
            // Newest first, the order the list is kept in, minus whatever has
            // sold out or gone away since it was looked at.
            this.render(
              this.withoutCartItems(
                cards.filter(function (card) {
                  return card;
                })
              )
            );
          }.bind(this)
        )
        .catch(function () {
          /* a row that cannot load is simply not shown */
        });
    }

    /**
     * A fixed collection and the recently-viewed list know nothing about the
     * cart, so they will happily offer what the shopper has already put in it.
     * The recommendations route is asked to leave those out; these two have to
     * be told here.
     */
    /**
     * What a fixed list may show: not what is in the cart, not another colour
     * of it, and one colour per style. A collection sorted by what sells
     * returns a style's colourways side by side, which in a row of eight
     * small cards reads as the same product four times. The recommendations
     * route is filtered the same way, in merge().
     */
    withoutCartItems(cards) {
      // Prototype-free, because the keys are product ids and product names
      // from the shop: a product called "constructor" would otherwise find a
      // truthy value waiting for it and be dropped as a duplicate.
      var inCart = Object.create(null);
      this.cartIds.forEach(function (id) {
        inCart[id] = true;
      });

      var doc = new DOMParser().parseFromString('<ul>' + cards.join('') + '</ul>', 'text/html');
      var taken = Object.create(null);
      var titles = Object.create(null);

      return Array.prototype.slice.call(doc.querySelectorAll('.cup-card'))
        .filter(function (card) {
          var id = card.dataset.id;
          if (!id || inCart[id] || taken[id]) return false;

          var siblings = (card.dataset.siblings || '').split(',').filter(Boolean);
          var ruled = siblings.some(function (sibling) {
            return inCart[sibling] || taken[sibling];
          });
          if (ruled) return false;

          // The sibling list is a metafield the office keeps, and it is not
          // complete: "מוצרי-החודש" returned the same tights and the same
          // baby suit twice, under the same name, as unrelated products. Two
          // cards with one name are one card to a shopper, whatever the shop
          // knows about them.
          var title = (card.dataset.title || '').trim();
          if (title) {
            if (titles[title]) return false;
            titles[title] = true;
          }

          taken[id] = true;
          return true;
        })
        .map(function (card) {
          return card.outerHTML;
        });
    }

    fetchFor(id) {
      var url =
        this.dataset.url +
        '?section_id=' +
        encodeURIComponent(this.dataset.section) +
        '&product_id=' +
        encodeURIComponent(id) +
        '&intent=related&limit=' +
        PER_SOURCE;

      return fetch(url)
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.prototype.slice.call(doc.querySelectorAll('.cup-card'));
        })
        .catch(function () {
          return [];
        });
    }

    /** One from each cart item in turn, so neither fills the row on its own. */
    merge(lists) {
      var inCart = {};
      var seen = {};
      var out = [];

      this.cartIds.forEach(function (id) {
        inCart[id] = true;
      });

      for (var depth = 0; out.length < MAX_CARDS; depth++) {
        var found = false;

        for (var list = 0; list < lists.length && out.length < MAX_CARDS; list++) {
          var card = lists[list][depth];
          if (!card) continue;
          found = true;

          var id = card.dataset.id;
          if (!id || seen[id] || inCart[id]) continue;

          var siblings = (card.dataset.siblings || '').split(',').filter(Boolean);
          var sameStyleInCart = siblings.some(function (sibling) {
            return inCart[sibling];
          });
          if (sameStyleInCart) continue;

          seen[id] = true;
          out.push(card);
        }

        if (!found) break;
      }

      return out;
    }

    render(cards) {
      if (!cards.length) return;
      this.track.innerHTML = cards.slice(0, this.limit).join('');
      this.classList.add('cart-upsell--ready');
      this.wakeFirstImages();

      sendEvent(
        'upsell_shown',
        this.rowParams({
          place: this.dataset.mode === 'test' ? 'test' : 'all',
          products: Math.min(cards.length, this.limit)
        })
      );
    }

    /* ------------------------------------------------------------------ *
     * Adding
     * ------------------------------------------------------------------ */

    onClick(event) {
      var size = event.target.closest('[data-upsell-size]');
      if (size) {
        event.preventDefault();
        this.add(size.dataset.upsellSize, size, this.sizes.dataset.for);
        return;
      }

      var add = event.target.closest('[data-upsell-add]');
      if (!add) return;

      event.preventDefault();
      var card = add.closest('.cup-card');
      var variants = this.variantsOf(card);

      if (!variants.length) return;

      // Always a choice, never an add behind the shopper's back: one size left
      // is still a size they should see before it is in their cart.
      this.openSizes(card, variants);
    }

    variantsOf(card) {
      return (card.dataset.variants || '')
        .split(';')
        .filter(Boolean)
        .map(function (record) {
          var parts = record.split('|');
          return { id: parts[0], label: parts[1], inStock: parts[2] === '1' };
        });
    }

    openSizes(card, variants) {
      var alreadyOpen = this.sizes.dataset.for === card.dataset.id && !this.sizes.hidden;
      if (alreadyOpen) {
        this.closeSizes();
        return;
      }

      this.sizesTitle.textContent = card.dataset.title || '';
      // Built as nodes rather than as a string: the label is a variant
      // title from the shop, and one with a quote in it would close the
      // attribute and spill the rest into the markup.
      this.sizesRow.textContent = '';
      variants.forEach(function (variant) {
        var node;
        if (variant.inStock) {
          node = document.createElement('button');
          node.type = 'button';
          node.className = 'cart-upsell__size';
          node.dataset.upsellSize = variant.id;
        } else {
          node = document.createElement('span');
          node.className = 'cart-upsell__size cart-upsell__size--gone';
          node.setAttribute('aria-disabled', 'true');
        }
        node.textContent = variant.label;
        this.sizesRow.appendChild(node);
      }, this);

      this.sizes.dataset.for = card.dataset.id;
      this.sizes.hidden = false;

      // The step between looking and buying: a shopper who opened the sizes
      // wanted this one. If plenty of these never become an add, the sizes
      // are what is losing them.
      sendEvent(
        'upsell_sizes',
        this.rowParams({
          item_name: card.dataset.title || '',
          sizes: variants.length,
          in_stock: variants.filter(function (variant) {
            return variant.inStock;
          }).length
        })
      );

      var first = this.sizesRow.querySelector('button');
      if (first) first.focus();
    }

    closeSizes() {
      this.sizes.hidden = true;
      this.sizes.dataset.for = '';
      this.sizesRow.innerHTML = '';
    }

    add(variantId, trigger, productId) {
      if (!variantId || this.adding) return;
      this.adding = true;
      trigger.classList.add('is-loading');

      // Whoever owns the cart on this page says which sections it needs back
      // to redraw itself: the drawer everywhere, the cart page on /cart, where
      // opening the drawer over the page the shopper is already reading would
      // be a strange thing to do.
      var drawer = this.closest('cart-drawer') ? document.querySelector('cart-drawer') : null;
      var items = drawer ? null : document.querySelector('cart-items');
      var owner = drawer || items || document.querySelector('cart-drawer');
      var sections = owner && typeof owner.getSectionsToRender === 'function' ? owner.getSectionsToRender() : [];

      // The attribution rides on the line, not on the cart: a shopper who
      // takes it out again should take the claim out with it. The leading
      // underscore keeps it out of the cart and checkout the shopper sees,
      // while the order still carries it.
      var body = {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            // Invisible to the shopper, and it travels into the order, so the
            // orders can be split by the row that sold the line without
            // trusting the browser to have reported anything. The collection
            // is named because there can be several; the other two rows are
            // one of a kind and the source already says which.
            properties: { _upsell: this.lineLabel() }
          }
        ]
      };
      if (sections.length) {
        body.sections = sections.map(function (section) {
          return section.section || section.id;
        });
        body.sections_url = window.location.pathname;
      }

      fetch(this.dataset.addUrl || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json();
        })
        .then(
          function (response) {
            if (response.status) throw new Error(response.description || response.message);

            this.closeSizes();
            if (this.status && response.items && response.items[0]) {
              this.status.textContent = response.items[0].title;
            }

            var added = response.items && response.items[0];
            if (added) {
              var params = this.rowParams({
                item_name: added.product_title,
                variant_id: added.variant_id
              });

              // The add response does not always carry a price; a missing one
              // is left out rather than reported as nothing.
              var price = added.final_price != null ? added.final_price : added.price;
              if (typeof price === 'number') {
                params.value = price / 100;
                params.currency =
                  window.Shopify && Shopify.currency ? Shopify.currency.active : 'ILS';
              }

              sendEvent('upsell_add', params);
            }

            if (owner === items) {
              this.redrawCartPage(items, sections, response);
              dropEverywhere(productId);
            } else if (owner && typeof owner.renderContents === 'function') {
              owner.renderContents(response);
            } else {
              window.location.reload();
            }
          }.bind(this)
        )
        .catch(
          function () {
            if (this.status) this.status.textContent = '';
          }.bind(this)
        )
        .finally(
          function () {
            this.adding = false;
            trigger.classList.remove('is-loading');
          }.bind(this)
        );
    }

    /** The cart page redraws its own sections, the way the theme does. */
    redrawCartPage(items, sections, response) {
      if (!response.sections) return;

      sections.forEach(function (section) {
        var holder = document.getElementById(section.id);
        var html = response.sections[section.section || section.id];
        if (!holder || !html) return;

        var target = holder.querySelector(section.selector) || holder;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector(section.selector || '.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      });

      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', response.item_count === 0);
    }
  }

  /**
   * The cart page does not rebuild the row itself, so once a product is in the
   * cart every row on the page drops it and remembers not to offer it again.
   */
  function dropEverywhere(productId) {
    if (!productId) return;

    document.querySelectorAll('cart-upsell').forEach(function (row) {
      var card = row.querySelector('.cup-card[data-id="' + productId + '"]');
      if (card) card.remove();

      var ids = (row.dataset.cartIds || '').split(',').filter(Boolean);
      if (ids.indexOf(productId) === -1) ids.push(productId);
      row.dataset.cartIds = ids.join(',');
      if (row.cartIds) row.cartIds = ids;
    });

    cache.clear();
  }

  customElements.define('cart-upsell', CartUpsell);
})();
